import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  Search, 
  Calendar,
  User,
  Activity,
  Shield,
  Download,
  Filter,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditLogs = () => {
  const { hasRole } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedResource, setSelectedResource] = useState('all');

  useEffect(() => {
    if (hasRole(['admin', 'auditor'])) {
      fetchAuditLogs();
    }
  }, [hasRole]);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit/logs`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action.toLowerCase()) {
      case 'login':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'logout':
        return <Info className="h-4 w-4 text-blue-600" />;
      case 'create_user':
      case 'upload_file':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'update_user':
      case 'detect_anomalies':
        return <Activity className="h-4 w-4 text-blue-600" />;
      case 'delete_user':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-800 border-green-200',
      logout: 'bg-blue-100 text-blue-800 border-blue-200',
      create_user: 'bg-green-100 text-green-800 border-green-200',
      update_user: 'bg-blue-100 text-blue-800 border-blue-200',
      delete_user: 'bg-red-100 text-red-800 border-red-200',
      upload_file: 'bg-purple-100 text-purple-800 border-purple-200',
      detect_anomalies: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    
    return (
      <Badge variant="outline" className={colors[action.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200'}>
        {action.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDetails = (details) => {
    if (!details) return 'No additional details';
    
    return Object.entries(details).map(([key, value]) => (
      `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`
    )).join(', ');
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDetails(log.details).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = selectedAction === 'all' || log.action.toLowerCase() === selectedAction;
    const matchesResource = selectedResource === 'all' || log.resource_type.toLowerCase() === selectedResource;
    
    return matchesSearch && matchesAction && matchesResource;
  });

  // Get unique actions and resource types for filters
  const uniqueActions = [...new Set(logs.map(log => log.action))];
  const uniqueResources = [...new Set(logs.map(log => log.resource_type))];

  if (!hasRole(['admin', 'auditor'])) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">Only administrators and auditors can access audit logs.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600 mt-2">Monitor system activity and user actions</p>
        </div>
        <Button variant="outline" onClick={() => toast.info('Export feature coming soon!')}>
          <Download className="h-4 w-4 mr-2" />
          Export Logs
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logs.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Today's Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {logs.filter(log => {
                const today = new Date().toDateString();
                return new Date(log.timestamp).toDateString() === today;
              }).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">User Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {logs.filter(log => ['create_user', 'update_user', 'login'].includes(log.action)).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">File Operations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {logs.filter(log => ['upload_file', 'detect_anomalies'].includes(log.action)).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter Logs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-logs-input"
                />
              </div>
            </div>
            
            <Select value={selectedAction} onValueChange={setSelectedAction}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {uniqueActions.map((action) => (
                  <SelectItem key={action} value={action.toLowerCase()}>
                    {action.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedResource} onValueChange={setSelectedResource}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {uniqueResources.map((resource) => (
                  <SelectItem key={resource} value={resource.toLowerCase()}>
                    {resource.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Activity Log
          </CardTitle>
          <CardDescription>
            Chronological record of all system activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Logs Found</h3>
              <p className="text-gray-600">
                {logs.length === 0 
                  ? 'No audit logs have been recorded yet.'
                  : 'No logs match your current filters.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredLogs.map((log) => (
                <div key={log.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    {getActionIcon(log.action)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      {getActionBadge(log.action)}
                      <Badge variant="outline" className="text-xs">
                        {log.resource_type}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        User ID: {log.user_id.slice(0, 8)}...
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                      {log.ip_address && (
                        <div className="flex items-center">
                          <Activity className="h-4 w-4 mr-1" />
                          {log.ip_address}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-700">
                      <strong>Resource:</strong> {log.resource_id}
                    </div>
                    
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="text-xs text-gray-600 mt-2 p-2 bg-white rounded border">
                        <strong>Details:</strong> {formatDetails(log.details)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center text-blue-900">
            <Info className="h-5 w-5 mr-2" />
            Audit Log Information
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-800 space-y-2">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium mb-2">Logged Actions:</h4>
              <ul className="space-y-1 text-sm">
                <li>• User authentication (login/logout)</li>
                <li>• User management (create/update/delete)</li>
                <li>• File operations (upload/process)</li>
                <li>• Anomaly detection activities</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Data Retention:</h4>
              <ul className="space-y-1 text-sm">
                <li>• Audit logs are retained for compliance</li>
                <li>• All timestamps are in UTC</li>
                <li>• IP addresses are logged for security</li>
                <li>• User actions are tracked by ID</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;